import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { PageLayout } from '../components/layout/PageLayout';
import { useTickets, useCreateTicket, type TicketCategory, type TicketStatus } from '../hooks';
import { useToast } from '../components/ui/toast';

interface FormData {
  category: TicketCategory;
  subject: string;
  detail: string;
  ref_note?: string;
}

export const CATEGORY_LABELS: Record<TicketCategory, string> = {
  complaint: 'ร้องเรียน',
  report: 'แจ้งเหตุ',
  grievance: 'ร้องทุกข์',
  leave_backdate: 'ขอลาย้อนหลัง',
  leave_type_change: 'ขอเปลี่ยนประเภทลา',
  leave_edit_locked: 'ขอแก้/ถอนใบลา (งวดปิดแล้ว)',
  schedule_change: 'ขอแก้กะ/ปฏิทิน',
};

export const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'เปิดเรื่อง',
  under_review: 'HR กำลังตรวจ',
  escalated: 'รอผู้บริหารอนุมัติ',
  approved: 'อนุมัติ',
  rejected: 'ไม่อนุมัติ',
  resolved: 'จบเรื่อง',
};

export const STATUS_COLOR: Record<TicketStatus, string> = {
  open: 'bg-gray-100 text-gray-700',
  under_review: 'bg-blue-100 text-blue-700',
  escalated: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  resolved: 'bg-green-100 text-green-700',
};

export function Tickets() {
  const { data: tickets, isLoading } = useTickets({ mine: true });
  const create = useCreateTicket();
  const { register, handleSubmit, reset } = useForm<FormData>({ defaultValues: { category: 'complaint' } });
  const [open, setOpen] = useState(false);
  const toast = useToast();

  const onSubmit = async (data: FormData) => {
    try {
      await create.mutateAsync({ ...data, ref_note: data.ref_note || undefined });
      reset();
      setOpen(false);
      toast.success('เปิดเรื่องแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  };

  return (
    <PageLayout title="เรื่องที่แจ้ง (Ticket)">
      {!open ? (
        <button onClick={() => setOpen(true)} className="btn-primary w-full mb-4">+ เปิดเรื่องใหม่</button>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="card p-4 space-y-3 mb-4">
          <div>
            <label className="label">ประเภท</label>
            <select {...register('category', { required: true })} className="input">
              {(Object.keys(CATEGORY_LABELS) as TicketCategory[]).map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">หัวข้อ</label>
            <input {...register('subject', { required: true })} className="input" placeholder="สรุปสั้นๆ" />
          </div>
          <div>
            <label className="label">รายละเอียด / เหตุผล</label>
            <textarea {...register('detail', { required: true })} rows={4} className="input" placeholder="อธิบายสิ่งที่อยากแจ้ง/ชี้แจง" />
          </div>
          <div>
            <label className="label">อ้างอิง (ถ้ามี)</label>
            <input {...register('ref_note')} className="input" placeholder="เช่น งวด 2026-04 / วันที่ลา" />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => { reset(); setOpen(false); }} className="btn-secondary flex-1">ยกเลิก</button>
            <button type="submit" disabled={create.isPending} className="btn-primary flex-1">
              {create.isPending ? 'กำลังส่ง…' : 'ส่งเรื่อง'}
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-500">กำลังโหลด…</p>
      ) : !tickets || tickets.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">ยังไม่มีเรื่องที่แจ้ง</p>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <div key={t.id} className="card p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs text-gray-500">{CATEGORY_LABELS[t.category]}</div>
                  <div className="font-medium truncate">{t.subject}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_COLOR[t.status]}`}>
                  {STATUS_LABELS[t.status]}
                </span>
              </div>
              {t.hr_note && <p className="text-xs text-gray-600 mt-2">HR: {t.hr_note}</p>}
              {t.decision_note && <p className="text-xs text-gray-700 mt-1">ผล: {t.decision_note}</p>}
            </div>
          ))}
        </div>
      )}
    </PageLayout>
  );
}
